/** Thin API client: JSON in/out, errors carry the server's message. */
export class ApiError extends Error {
	constructor(
		message: string,
		readonly status: number,
		readonly data: any,
	) {
		super(message);
	}
}

export async function api<T = any>(method: string, path: string, body?: unknown): Promise<T> {
	const init: RequestInit = { method, credentials: 'same-origin', headers: {} };
	if (body instanceof FormData) init.body = body;
	else if (body !== undefined) {
		(init.headers as Record<string, string>)['content-type'] = 'application/json';
		init.body = JSON.stringify(body);
	}
	const res = await fetch(path, init);
	const text = await res.text();
	let data: any = null;
	try {
		data = text ? JSON.parse(text) : null;
	} catch {
		data = text;
	}
	if (!res.ok) throw new ApiError(data?.error ?? `Request failed (${res.status})`, res.status, data);
	return data as T;
}

export const get = <T = any>(p: string) => api<T>('GET', p);
export const post = <T = any>(p: string, b: unknown = {}) => api<T>('POST', p, b);
export const put = <T = any>(p: string, b: unknown) => api<T>('PUT', p, b);
export const patch = <T = any>(p: string, b: unknown) => api<T>('PATCH', p, b);
export const del = <T = any>(p: string) => api<T>('DELETE', p);

// ------------------------------------------------------------------ passkeys

const b64uToBuf = (s: string) => {
	const b = atob(s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4));
	const out = new Uint8Array(b.length);
	for (let i = 0; i < b.length; i++) out[i] = b.charCodeAt(i);
	return out.buffer;
};
const bufToB64u = (buf: ArrayBuffer) => {
	const b = new Uint8Array(buf);
	let s = '';
	for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]);
	return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export function passkeysSupported(): boolean {
	return typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials;
}

function creationOptions(pk: any): PublicKeyCredentialCreationOptions {
	return {
		...pk,
		challenge: b64uToBuf(pk.challenge),
		user: { ...pk.user, id: b64uToBuf(pk.user.id) },
		excludeCredentials: (pk.excludeCredentials ?? []).map((c: any) => ({ ...c, id: b64uToBuf(c.id) })),
	};
}

function serializeAttestation(cred: PublicKeyCredential) {
	const r = cred.response as AuthenticatorAttestationResponse;
	return {
		id: cred.id,
		rawId: bufToB64u(cred.rawId),
		type: cred.type,
		response: {
			clientDataJSON: bufToB64u(r.clientDataJSON),
			attestationObject: bufToB64u(r.attestationObject),
			transports: typeof r.getTransports === 'function' ? r.getTransports() : [],
		},
	};
}

/** Create account: the new passkey is the account. */
export async function registerPasskey(displayName?: string) {
	const opts = await post('/api/auth/register/options', displayName ? { displayName } : {});
	const cred = (await navigator.credentials.create({ publicKey: creationOptions(opts.publicKey) })) as PublicKeyCredential | null;
	if (!cred) throw new Error('No passkey was created.');
	return post('/api/auth/register/verify', { challengeId: opts.challengeId, credential: serializeAttestation(cred), passkeyName: deviceName() });
}

export async function signInWithPasskey() {
	const opts = await post('/api/auth/login/options');
	const pk = opts.publicKey;
	const cred = (await navigator.credentials.get({ publicKey: { ...pk, challenge: b64uToBuf(pk.challenge), allowCredentials: [] } })) as PublicKeyCredential | null;
	if (!cred) throw new Error('No passkey was chosen.');
	const r = cred.response as AuthenticatorAssertionResponse;
	return post('/api/auth/login/verify', {
		challengeId: opts.challengeId,
		credential: {
			id: cred.id,
			rawId: bufToB64u(cred.rawId),
			type: cred.type,
			response: {
				clientDataJSON: bufToB64u(r.clientDataJSON),
				authenticatorData: bufToB64u(r.authenticatorData),
				signature: bufToB64u(r.signature),
				userHandle: r.userHandle ? bufToB64u(r.userHandle) : null,
			},
		},
	});
}

export async function addPasskey() {
	const opts = await post('/api/auth/passkeys/options');
	const cred = (await navigator.credentials.create({ publicKey: creationOptions(opts.publicKey) })) as PublicKeyCredential | null;
	if (!cred) throw new Error('No passkey was created.');
	return post('/api/auth/passkeys/verify', { challengeId: opts.challengeId, credential: serializeAttestation(cred), passkeyName: deviceName() });
}

function deviceName(): string {
	const ua = navigator.userAgent;
	if (/iPhone|iPad/.test(ua)) return 'iPhone / iPad';
	if (/Android/.test(ua)) return 'Android';
	if (/Mac/.test(ua)) return 'Mac';
	if (/Windows/.test(ua)) return 'Windows';
	return 'Passkey';
}

export function media(id: string | null | undefined, w?: number): string | undefined {
	return id ? `/media/${id}${w ? `?w=${w}` : ''}` : undefined;
}

export function formatCode(code: string): string {
	return code.length === 6 ? `${code.slice(0, 4)}-${code.slice(4)}` : code;
}
